import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement,
  RadialLinearScale, PointElement, LineElement,
  Filler, Tooltip, Legend,
  ChartData, ChartOptions
} from 'chart.js';
import { Bar, Radar } from 'react-chartjs-2';
import { GlassCard } from '@/components/common/GlassCard';
import './ExecutiveView.css';
import {
  useAnalyticsSummary,
  useAnalyticsFundamentals,
} from '@/hooks/useAnalytics';

ChartJS.register(CategoryScale, LinearScale, BarElement, RadialLinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

type AnalyticsTab = 'summary' | 'fundamentals' | 'heatmap' | 'ona' | 'generational' | 'bias' | 'omissions';

const TABS: Array<{ id: AnalyticsTab; icon: string; labelKey: string }> = [
  { id: 'summary',      icon: 'fa-chart-pie',      labelKey: 'analytics.tabs.summary'      },
  { id: 'fundamentals', icon: 'fa-layer-group',    labelKey: 'analytics.tabs.fundamentals' },
  { id: 'heatmap',      icon: 'fa-table-cells',    labelKey: 'analytics.tabs.heatmap'      },
  { id: 'ona',          icon: 'fa-share-nodes',    labelKey: 'analytics.ona.title'         },
  { id: 'generational', icon: 'fa-people-group',   labelKey: 'analytics.tabs.generational' },
  { id: 'bias',         icon: 'fa-scale-balanced', labelKey: 'analytics.bias'              },
  { id: 'omissions',    icon: 'fa-eye-slash',      labelKey: 'analytics.omissions'         },
];

// Note: FUNDAMENTALS are mock data labels — they come from the domain catalog.
// They are not UI text and thus remain as mock strings here.
const FUNDAMENTALS = [
  'Accountability', 'Colaboración', 'Innovación', 'Comunicación', 'Liderazgo', 'Orient. Cliente',
];

const MOCK_FUND_SCORES = [3.5, 3.2, 3.8, 3.1, 3.4, 3.6];

// Fixed mock heatmap data — deterministic values for consistency across renders/tests
const HEATMAP_PERSONS = ['Ana G.', 'Luis M.', 'Petra R.', 'Carlos D.', 'María L.'];
const HEATMAP_SCORES: number[][] = [
  [3.2, 3.8, 2.9, 3.5, 3.1, 3.7],
  [2.8, 3.1, 3.5, 2.7, 3.4, 2.9],
  [3.6, 2.9, 3.2, 3.8, 2.6, 3.3],
  [3.0, 3.4, 2.8, 3.1, 3.7, 3.2],
  [3.5, 3.0, 3.6, 2.9, 3.2, 3.5],
];

const MOCK_KPI = {
  avgScore: 3.4,
  responses: 148,
  participants: 32,
  completionRate: 87,
};

interface SummaryTabProps {
  avgScore?: number;
  responses?: number;
  participants?: number;
  completionRate?: number;
  fundLabels?: string[];
  fundScores?: number[];
}

function SummaryTab({ avgScore, responses, participants, completionRate, fundLabels, fundScores }: SummaryTabProps) {
  const { t } = useTranslation();

  const labels = fundLabels ?? FUNDAMENTALS;
  const scores = fundScores ?? MOCK_FUND_SCORES;

  const barData: ChartData<'bar'> = {
    labels,
    datasets: [{
      label: t('analytics.score'),
      data: scores,
      backgroundColor: [
        'rgba(52,47,130,0.7)', 'rgba(64,184,233,0.7)', 'rgba(32,177,90,0.7)',
        'rgba(240,133,33,0.7)', 'rgba(239,83,149,0.7)', 'rgba(225,31,38,0.7)',
      ],
      borderRadius: 6,
    }],
  };

  const barOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      y: { min: 0, max: 4, ticks: { stepSize: 1 } },
    },
  };

  const kpiAvgScore = avgScore ?? MOCK_KPI.avgScore;
  const kpiResponses = responses ?? MOCK_KPI.responses;
  const kpiParticipants = participants ?? MOCK_KPI.participants;
  const kpiCompletionRate = completionRate ?? MOCK_KPI.completionRate;

  return (
    <div className="exec-tab-content">
      {/* KPI strip */}
      <div className="exec-kpi-strip">
        {[
          { label: t('analytics.score'),         value: kpiAvgScore.toFixed(1),       icon: 'fa-star',      color: 'var(--cp-purple)' },
          { label: t('analytics.responses'),      value: kpiResponses,                 icon: 'fa-clipboard', color: 'var(--cp-cyan)' },
          { label: t('analytics.participants'),   value: kpiParticipants,              icon: 'fa-users',     color: 'var(--cp-green)' },
          { label: t('analytics.completionRate'), value: `${kpiCompletionRate}%`,      icon: 'fa-percent',   color: 'var(--cp-orange)' },
        ].map((kpi) => (
          <GlassCard key={kpi.icon} className="exec-kpi-card">
            <div className="exec-kpi-icon" style={{ color: kpi.color, background: `${kpi.color}18` }}>
              <i className={`fas ${kpi.icon}`} />
            </div>
            <div className="exec-kpi-body">
              <span className="exec-kpi-value">{kpi.value}</span>
              <span className="exec-kpi-label">{kpi.label}</span>
            </div>
          </GlassCard>
        ))}
      </div>

      {/* Bar chart */}
      <GlassCard className="exec-chart-card">
        <div className="exec-chart-title">
          <i className="fas fa-bar-chart" />
          {t('analytics.tabs.fundamentals')} — {t('analytics.avgByCategory')}
        </div>
        <div className="exec-chart-body">
          <Bar data={barData} options={barOptions} />
        </div>
      </GlassCard>
    </div>
  );
}

interface FundamentalsTabProps {
  fundLabels?: string[];
  fundScores?: number[];
}

function FundamentalsTab({ fundLabels, fundScores }: FundamentalsTabProps) {
  const { t } = useTranslation();

  const labels = fundLabels ?? FUNDAMENTALS;
  const scores = fundScores ?? MOCK_FUND_SCORES;

  const radarData: ChartData<'radar'> = {
    labels,
    datasets: [{
      label: t('analytics.organization'),
      data: scores,
      borderColor: '#342F82',
      backgroundColor: 'rgba(52,47,130,0.18)',
      pointBackgroundColor: '#342F82',
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
    }],
  };

  const radarOptions: ChartOptions<'radar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, padding: 20 } } },
    scales: {
      r: { min: 0, max: 4, ticks: { stepSize: 1, display: false }, pointLabels: { font: { size: 11 } } },
    },
  };

  return (
    <div className="exec-tab-content">
      <div className="exec-charts-row">
        <GlassCard className="exec-chart-card">
          <div className="exec-chart-title"><i className="fas fa-star" /> {t('analytics.fundamentalsRadar')}</div>
          <div className="exec-chart-body"><Radar data={radarData} options={radarOptions} /></div>
        </GlassCard>

        <div className="exec-fund-list">
          {labels.map((f, i) => {
            const score = scores[i] ?? 0;
            return (
              <GlassCard key={f} className="exec-fund-row">
                <span className="exec-fund-name">{f}</span>
                <div className="exec-fund-bar-bg">
                  <div className="exec-fund-bar-fill" style={{ width: `${(score / 4) * 100}%` }} />
                </div>
                <span className="exec-fund-score">{score.toFixed(1)}</span>
              </GlassCard>
            );
          })}
        </div>
      </div>
    </div>
  );
}

interface HeatmapTabProps {
  /** HeatmapCell[] from API — if provided, replaces static mock */
  cells?: Array<{ ou: string; fundamental: string; score: number }>;
  fundLabels?: string[];
}

function HeatmapTab({ cells, fundLabels }: HeatmapTabProps) {
  const { t } = useTranslation();

  function heatColor(score: number): string {
    if (score >= 3.5) return 'rgba(32,177,90,0.7)';
    if (score >= 2.5) return 'rgba(64,184,233,0.6)';
    if (score >= 1.5) return 'rgba(240,133,33,0.6)';
    return 'rgba(225,31,38,0.6)';
  }

  // If real API data is available, pivot it; otherwise fall back to static mock
  const hasCells = cells && cells.length > 0;
  const persons = hasCells
    ? [...new Set(cells!.map(c => c.ou))]
    : HEATMAP_PERSONS;
  const labels = fundLabels ?? (hasCells ? [...new Set(cells!.map(c => c.fundamental))] : FUNDAMENTALS);

  function getScore(person: string, fund: string): number | undefined {
    if (hasCells) {
      return cells!.find(c => c.ou === person && c.fundamental === fund)?.score;
    }
    const pi = HEATMAP_PERSONS.indexOf(person);
    const fi = FUNDAMENTALS.indexOf(fund);
    return HEATMAP_SCORES[pi]?.[fi];
  }

  return (
    <div className="exec-tab-content">
      <GlassCard className="exec-heatmap-card">
        <div className="exec-chart-title"><i className="fas fa-table-cells" /> {t('analytics.heatmapPersonFund')}</div>
        <div className="exec-heatmap-wrap">
          <table className="exec-heatmap-table">
            <thead>
              <tr>
                <th></th>
                {labels.map(f => <th key={f}>{f}</th>)}
              </tr>
            </thead>
            <tbody>
              {persons.map(p => (
                <tr key={p}>
                  <td className="exec-heatmap-person">{p}</td>
                  {labels.map(f => {
                    const s = getScore(p, f);
                    return (
                      <td key={f} className="exec-heatmap-cell" style={{ background: heatColor(s ?? 0) }}>
                        {s?.toFixed(1)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="exec-heatmap-legend">
          <span style={{ background: 'rgba(225,31,38,0.6)' }}>1.0–1.4</span>
          <span style={{ background: 'rgba(240,133,33,0.6)' }}>1.5–2.4</span>
          <span style={{ background: 'rgba(64,184,233,0.6)' }}>2.5–3.4</span>
          <span style={{ background: 'rgba(32,177,90,0.7)' }}>3.5–4.0</span>
        </div>
      </GlassCard>
    </div>
  );
}

function PlaceholderTab({ label }: { label: string }) {
  const { t } = useTranslation();
  return (
    <div className="exec-tab-content exec-placeholder">
      <i className="fas fa-chart-network" />
      <p>{label} — {t('analytics.moduleInDev')}</p>
    </div>
  );
}

const EXEC_PERIODS = ['2026-01', '2025-12', '2025-q4'];

function fmtExecPeriod(value: string): string {
  if (value.includes('q')) {
    const [y, q] = value.split('-q');
    return `Q${q} ${y}`;
  }
  const [y, m] = value.split('-');
  if (!y || !m) return value;
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleString(undefined, { month: 'long', year: 'numeric' });
}

export function ExecutiveView() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('summary');
  const [period, setPeriod] = useState('2026-01');
  const [ou, setOu] = useState('all');

  const { data: summaryData } = useAnalyticsSummary();
  const { data: fundamentalsData } = useAnalyticsFundamentals();

  // Derive labels + scores from fundamentals API response (or fall back to mocks)
  const fundLabels = fundamentalsData && fundamentalsData.length > 0
    ? fundamentalsData.map(f => f.name)
    : undefined;
  const fundScores = fundamentalsData && fundamentalsData.length > 0
    ? fundamentalsData.map(f => f.score)
    : undefined;

  return (
    <div className="executive-view">
      {/* Header */}
      <div className="exec-header glass">
        <div>
          <h1 className="exec-title">{t('analytics.title')}</h1>
          <p className="exec-subtitle">{t('analytics.subtitle')} — {t('analytics.executiveSubtitle')}</p>
        </div>
        <div className="exec-filters">
          <select
            className="exec-select"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            aria-label={t('analytics.period')}
          >
            {EXEC_PERIODS.map(p => (
              <option key={p} value={p}>{fmtExecPeriod(p)}</option>
            ))}
          </select>
          <select
            className="exec-select"
            value={ou}
            onChange={e => setOu(e.target.value)}
            aria-label={t('analytics.ou')}
          >
            <option value="all">{t('analytics.filterBar.allOrg')}</option>
            <option value="tech">{t('analytics.exec.ouTech')}</option>
            <option value="ops">{t('analytics.exec.ouOps')}</option>
            <option value="sales">{t('analytics.exec.ouSales')}</option>
          </select>
        </div>
      </div>

      {/* Tab bar */}
      <div className="exec-tab-bar glass">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`exec-tab-btn${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <i className={`fas ${tab.icon}`} aria-hidden="true" />
            <span>{t(tab.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="exec-content">
        {activeTab === 'summary'      && (
          <SummaryTab
            avgScore={summaryData?.avgScore}
            responses={summaryData?.totalEvaluations}
            participants={summaryData?.uniqueEvaluators}
            completionRate={summaryData?.responseRate}
            fundLabels={fundLabels}
            fundScores={fundScores}
          />
        )}
        {activeTab === 'fundamentals' && (
          <FundamentalsTab fundLabels={fundLabels} fundScores={fundScores} />
        )}
        {activeTab === 'heatmap'      && (
          <HeatmapTab fundLabels={fundLabels} />
        )}
        {activeTab === 'ona'          && <PlaceholderTab label={t('analytics.ona.title')} />}
        {activeTab === 'generational' && <PlaceholderTab label={t('analytics.tabs.generational')} />}
        {activeTab === 'bias'         && <PlaceholderTab label={t('analytics.bias')} />}
        {activeTab === 'omissions'    && <PlaceholderTab label={t('analytics.omissions')} />}
      </div>
    </div>
  );
}
